import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { resolveUiLocale } from '@/utils/uiLocale';

import type { ForecastChartPoint } from '../types/forecast.types';
import {
  type ChartGranularity,
  formatChartBucketLabel,
  formatChartTooltipLabel,
} from '../utils/chartLabels';

function toNumber(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  return Number.isFinite(value) ? value : null;
}

interface Props {
  data: ForecastChartPoint[];
  currentPeriod?: string | null;
  granularity?: ChartGranularity;
  className?: string;
  height?: number;
}

export function ForecastChart({
  data,
  currentPeriod,
  granularity = 'week',
  className = '',
  height = 400,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = resolveUiLocale(i18n.language);

  const points = data.map((p) => ({
    time_bucket: p.time_bucket,
    actual: toNumber(p.actual_qty),
    forecast: toNumber(p.forecast_qty),
    lower: toNumber(p.lower_bound_qty),
    upper: toNumber(p.upper_bound_qty),
  }));

  const formatX = (value: string) => formatChartBucketLabel(value, granularity, locale);

  return (
    <div className={`min-w-0 w-full max-w-full ${className}`} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="time_bucket"
            tickFormatter={formatX}
            tick={{ fontSize: 10, fill: '#64748b' }}
            minTickGap={32}
            interval="preserveStartEnd"
            angle={granularity === 'week' ? -35 : 0}
            textAnchor={granularity === 'week' ? 'end' : 'middle'}
            height={granularity === 'week' ? 56 : 32}
          />
          <YAxis tick={{ fontSize: 11, fill: '#64748b' }} width={56} />
          <Tooltip
            labelFormatter={(value) =>
              formatChartTooltipLabel(String(value), granularity, locale)
            }
            formatter={(value: number, name: string) => [
              Number(value).toLocaleString(locale, { maximumFractionDigits: 0 }),
              name,
            ]}
          />
          <Legend />
          {currentPeriod ? (
            <ReferenceLine
              x={currentPeriod}
              stroke="#94a3b8"
              strokeDasharray="4 4"
              label={{
                value:
                  granularity === 'month'
                    ? t('forecastPage.currentMonth')
                    : t('forecastPage.currentWeek'),
                position: 'insideTopRight',
                fontSize: 10,
                fill: '#64748b',
              }}
            />
          ) : null}
          <Line
            type="monotone"
            dataKey="actual"
            name={t('forecastPage.history')}
            stroke="#64748b"
            strokeWidth={2}
            dot={false}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="forecast"
            name={t('forecastPage.forecast')}
            stroke="#0ea5e9"
            strokeWidth={2}
            dot={false}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="lower"
            name={t('forecastPage.lowerBound')}
            stroke="#7dd3fc"
            strokeWidth={1}
            strokeDasharray="4 4"
            dot={false}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="upper"
            name={t('forecastPage.upperBound')}
            stroke="#7dd3fc"
            strokeWidth={1}
            strokeDasharray="4 4"
            dot={false}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
